import React, { useEffect } from 'react';
import { AlertTriangle, AlertCircle, XCircle, ChevronRight, User } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useSubscriptionStore, UsageMetrics } from '../../stores/subscriptionStore';

interface UsageLimitBannerProps {
  className?: string;
}

export const UsageLimitBanner: React.FC<UsageLimitBannerProps> = ({ className = '' }) => {
  const navigate = useNavigate();
  const { usage, usageLoading, fetchUsage, personalUsage, personalUsageLoading, fetchPersonalUsage } = useSubscriptionStore();

  useEffect(() => {
    fetchUsage();
    fetchPersonalUsage();
  }, []);

  if ((usageLoading && personalUsageLoading) || (!usage && !personalUsage)) return null;

  // --- Personal-level alert (takes priority) ---
  const personalAlert = getPersonalAlert(personalUsage);
  if (personalAlert) {
    return (
      <div className={`${personalAlert.bgColor} border rounded-lg p-4 ${className}`}>
        <div className="flex items-start">
          <div className="flex-shrink-0">
            {personalAlert.icon}
          </div>
          <div className="ml-3 flex-1">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 opacity-60" />
              <h3 className={`text-sm font-medium ${personalAlert.textColor}`}>
                {personalAlert.title}
              </h3>
            </div>
            <p className={`mt-1 text-sm ${personalAlert.textColor} opacity-80`}>
              {personalAlert.message}
            </p>
            <div className="mt-3 flex space-x-4">
              <button
                onClick={() => navigate('/my-usage')}
                className={`text-sm font-medium ${personalAlert.textColor} hover:underline flex items-center`}
              >
                View My Usage
                <ChevronRight className="h-4 w-4 ml-1" />
              </button>
            </div>
          </div>
          <div className="ml-4 flex-shrink-0">
            <span className={`px-2 py-1 rounded text-xs ${personalAlert.badgeClass}`}>
              {personalAlert.badge}
            </span>
          </div>
        </div>
      </div>
    );
  }

  // --- Org-level alert (fallback) ---
  if (!usage) return null;

  const getAlertLevel = (metrics: UsageMetrics): 'none' | 'warning' | 'danger' | 'critical' => {
    if (metrics.shouldBlock) return 'critical';
    if (metrics.isInOverage) return 'danger';

    const maxPercent = Math.max(
      metrics.encounters.isUnlimited ? 0 : metrics.encounters.percentUsed,
      metrics.minutes.isUnlimited ? 0 : metrics.minutes.percentUsed
    );

    if (maxPercent >= 90) return 'danger';
    if (maxPercent >= 80) return 'warning';
    return 'none';
  };

  const alertLevel = getAlertLevel(usage);

  if (alertLevel === 'none') return null;

  const getBannerConfig = () => {
    switch (alertLevel) {
      case 'critical':
        return {
          bgColor: 'bg-red-50 border-red-200',
          textColor: 'text-red-800',
          icon: <XCircle className="h-5 w-5 text-red-500" />,
          title: 'Subscription Limit Reached',
          message: usage.blockReason || 'You have reached your subscription limit. New recordings may be blocked.'
        };
      case 'danger':
        return {
          bgColor: 'bg-red-50 border-red-200',
          textColor: 'text-red-800',
          icon: <AlertCircle className="h-5 w-5 text-red-500" />,
          title: usage.isInOverage ? 'Overage Active' : 'Approaching Limit',
          message: usage.isInOverage
            ? `You are in overage. Current overage charges: $${usage.currentOverageAmount.toFixed(2)}`
            : 'You have used 90% or more of your subscription limits.'
        };
      case 'warning':
        return {
          bgColor: 'bg-amber-50 border-amber-200',
          textColor: 'text-amber-800',
          icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
          title: 'Usage Alert',
          message: 'You have used 80% or more of your subscription limits this period.'
        };
      default:
        return null;
    }
  };

  const config = getBannerConfig();
  if (!config) return null;

  return (
    <div className={`${config.bgColor} border rounded-lg p-4 ${className}`}>
      <div className="flex items-start">
        <div className="flex-shrink-0">
          {config.icon}
        </div>
        <div className="ml-3 flex-1">
          <h3 className={`text-sm font-medium ${config.textColor}`}>
            {config.title}
          </h3>
          <p className={`mt-1 text-sm ${config.textColor} opacity-80`}>
            {config.message}
          </p>
          <div className="mt-3 flex space-x-4">
            <button
              onClick={() => navigate('/organization/usage')}
              className={`text-sm font-medium ${config.textColor} hover:underline flex items-center`}
            >
              View Usage Details
              <ChevronRight className="h-4 w-4 ml-1" />
            </button>
          </div>
        </div>
        <div className="ml-4 flex-shrink-0">
          <div className="flex space-x-2 text-xs">
            {!usage.encounters.isUnlimited && (
              <span className={`px-2 py-1 rounded ${usage.encounters.percentUsed >= 80 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                Encounters: {Math.round(usage.encounters.percentUsed)}%
              </span>
            )}
            {!usage.minutes.isUnlimited && (
              <span className={`px-2 py-1 rounded ${usage.minutes.percentUsed >= 80 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                Minutes: {Math.round(usage.minutes.percentUsed)}%
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Determine personal-level alert based on personal usage data.
 * Returns null if no personal alert is needed (feature disabled or under thresholds).
 */
function getPersonalAlert(personalUsage: ReturnType<typeof useSubscriptionStore.getState>['personalUsage']) {
  if (!personalUsage) return null;

  const { myUsage } = personalUsage;

  // No personal limits active (feature flag off or unlimited)
  if (!myUsage.personalEncounterLimit || myUsage.personalEncounterLimit <= 0) return null;

  const percent = myUsage.personalEncounterPercentUsed;
  const used = myUsage.encounters;
  const limit = myUsage.personalEncounterLimit;
  const remaining = myUsage.personalEncounterRemaining;

  // 100%+ - At or over personal limit
  if (myUsage.isOverPersonalLimit) {
    if (myUsage.overagesAllowed) {
      return {
        bgColor: 'bg-amber-50 border-amber-200',
        textColor: 'text-amber-800',
        icon: <AlertCircle className="h-5 w-5 text-amber-500" />,
        title: 'Personal Encounter Limit Reached',
        message: `You've used all ${limit} encounters. ${myUsage.overageEncounters} overage encounter${myUsage.overageEncounters !== 1 ? 's' : ''} at $${myUsage.overagePrice.toFixed(2)} each ($${(myUsage.overageEncounters * myUsage.overagePrice).toFixed(2)} total).`,
        badge: `${used} / ${limit}`,
        badgeClass: 'bg-amber-100 text-amber-700'
      };
    }
    return {
      bgColor: 'bg-red-50 border-red-200',
      textColor: 'text-red-800',
      icon: <XCircle className="h-5 w-5 text-red-500" />,
      title: 'Personal Encounter Limit Reached',
      message: `You've reached your personal limit of ${limit} encounters. New recordings are blocked until the next billing period.`,
      badge: `${used} / ${limit}`,
      badgeClass: 'bg-red-100 text-red-700'
    };
  }

  // 90%+ - Approaching limit
  if (percent >= 90) {
    return {
      bgColor: 'bg-red-50 border-red-200',
      textColor: 'text-red-800',
      icon: <AlertCircle className="h-5 w-5 text-red-500" />,
      title: 'Approaching Personal Limit',
      message: `You're approaching your encounter limit (${used}/${limit} used). ${remaining} encounter${remaining !== 1 ? 's' : ''} remaining this period.`,
      badge: `${Math.round(percent)}%`,
      badgeClass: 'bg-red-100 text-red-700'
    };
  }

  // 80%+ - Warning
  if (percent >= 80) {
    return {
      bgColor: 'bg-amber-50 border-amber-200',
      textColor: 'text-amber-800',
      icon: <AlertTriangle className="h-5 w-5 text-amber-500" />,
      title: 'Personal Usage Alert',
      message: `You've used ${Math.round(percent)}% of your personal encounter limit (${used}/${limit}). ${remaining} encounter${remaining !== 1 ? 's' : ''} remaining.`,
      badge: `${Math.round(percent)}%`,
      badgeClass: 'bg-amber-100 text-amber-700'
    };
  }

  return null;
}

export default UsageLimitBanner;
